/**
 * Zuora Reconciliation Queue — admin monitoring service.
 *
 * Read-only views over the object-aware Zuora pipeline:
 *  - zuora_reconciliation_queue_summary (security-definer view → accurate
 *    aggregates; status/reason/count/oldest/older_than_24h)
 *  - zuora_reconciliation_queue (row detail; RLS-gated to admin/superadmin)
 *  - zuora_webhook_events object-stream ingest health (object_class IS NOT NULL
 *    distinguishes object events from legacy basicInfo events)
 *
 * The page that consumes this is admin/superadmin gated at the route level.
 * Until the Zuora object-stream format switch (cutover step "B") is live on
 * production, the queue is dormant and these reads return empty/zero — which is
 * the expected healthy state.
 */

import { supabase } from "./supabase";

export interface ReconQueueSummaryRow {
  status: string;
  reason: string | null;
  n: number;
  oldest: string | null;
  older_than_24h: number;
}

export interface ReconQueueRow {
  id: string;
  zuora_account_id: string | null;
  zuora_contact_id: string | null;
  object_type: string | null;
  object_id: string | null;
  reason: string | null;
  status: string;
  attempt_count: number;
  last_error: string | null;
  last_wait_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface ObjectIngestStats {
  failed: number;
  pending: number;
  processed: number;
}

export async function getReconQueueSummary(): Promise<ReconQueueSummaryRow[]> {
  const { data, error } = await supabase
    .from("zuora_reconciliation_queue_summary")
    .select("*")
    .order("status", { ascending: true })
    .order("reason", { ascending: true });
  if (error) {
    throw new Error(error.message);
  }
  return (data ?? []) as ReconQueueSummaryRow[];
}

/**
 * Blocked rows + anything still pending, oldest first. RLS on
 * zuora_reconciliation_queue restricts this to admin/superadmin.
 */
export async function getBlockedAndPendingRows(): Promise<ReconQueueRow[]> {
  const { data, error } = await supabase
    .from("zuora_reconciliation_queue")
    .select(
      "id, zuora_account_id, zuora_contact_id, object_type, object_id, reason, status, attempt_count, last_error, last_wait_reason, created_at, updated_at",
    )
    .in("status", ["blocked", "pending"])
    .order("created_at", { ascending: true })
    .limit(200);
  if (error) {
    throw new Error(error.message);
  }
  return (data ?? []) as ReconQueueRow[];
}

/**
 * Object-stream ingest health from zuora_webhook_events. Object events carry a
 * non-null object_class; legacy basicInfo events do not, so the filter scopes
 * counts to the new pipeline only. A rising `failed` count usually means a new
 * Zuora object type the normalizer doesn't recognise yet.
 */
export async function getObjectIngestStats(): Promise<ObjectIngestStats> {
  const countFor = async (status: string): Promise<number> => {
    const { count, error } = await supabase
      .from("zuora_webhook_events")
      .select("*", { count: "exact", head: true })
      .not("object_class", "is", null)
      .eq("status", status);
    if (error) {
      throw new Error(error.message);
    }
    return count ?? 0;
  };
  const [failed, pending, processed] = await Promise.all([
    countFor("failed"),
    countFor("pending"),
    countFor("processed"),
  ]);
  return { failed, pending, processed };
}

/**
 * Worker-health snapshot from public.zuora_worker_health(p_window interval).
 *
 * This is the RAMP GATE for the Zuora reconcile stage (see the phone-fill
 * misattribution incident): `alarm` is the single boolean an operator checks
 * before turning ZUORA_RECONCILE_ENABLED up. Until this panel shipped nothing
 * in the app consumed the function, which is why the ramp stayed blocked.
 *
 * The RPC is SECURITY DEFINER and gates itself to service_role or an ACTIVE
 * profile with role in ('admin','superadmin'), raising `not_authorized`
 * otherwise; the underlying _zuora_worker_health_raw is NOT granted to
 * `authenticated`, so the gate cannot be side-stepped. The route guard is a
 * convenience, not the enforcement.
 */
export interface ZuoraWorkerHealth {
  /** The window echoed back as a Postgres interval string, e.g. "01:00:00". */
  window: string;
  runs: number;
  ok: number;
  degraded: number;
  failed: number;
  claim_failed: number;
  reconcile_failed: number;
  reconcile_stopped_early: number;
  /** Deliberately UNWINDOWED: "when did the worker last run at all". */
  last_run_at: string | null;
  last_run_age_seconds: number | null;
  /** null when runs = 0 (no denominator), not 0 — render as "—", never "0%". */
  ok_ratio: number | null;
  queue_pending: number;
  // The five alarm conditions, each independently reported so the panel can
  // say WHICH one tripped rather than just that something did.
  no_runs: boolean;
  heartbeat_stale: boolean;
  any_failed: boolean;
  silent_stall: boolean;
  degraded_sustained: boolean;
  /** OR of the five above. false = safe to ramp. */
  alarm: boolean;
}

/** Windows offered by the panel. Value is a Postgres interval literal. */
export const WORKER_HEALTH_WINDOWS = [
  { label: "15 min", value: "15 minutes" },
  { label: "1 hour", value: "1 hour" },
  { label: "6 hours", value: "6 hours" },
  { label: "24 hours", value: "24 hours" },
] as const;

export async function getZuoraWorkerHealth(
  windowInterval: string,
): Promise<ZuoraWorkerHealth> {
  const { data, error } = await supabase.rpc("zuora_worker_health", {
    p_window: windowInterval,
  });
  if (error) {
    // The RPC raises `not_authorized` for a non-admin/inactive profile. Say so
    // plainly rather than leaking a raw Postgres error at the operator.
    if ((error.message ?? "").includes("not_authorized")) {
      throw new Error(
        "Not authorized to read Zuora worker health (requires an active admin or superadmin profile).",
      );
    }
    throw new Error(error.message);
  }
  return data as ZuoraWorkerHealth;
}
